import { useEffect, useState } from 'react';
import Hls from 'hls.js';
import { resolveClipUrl } from '../utils/movie';
import { getAssetPlaybackUrl } from '../data/uploadService';

// Global capture concurrency limiter — grabbing one frame per clip means many
// short-lived decoders; cap how many run at once so a long movie doesn't open
// dozens of simultaneous video/HLS pipelines.
let activeCaptures = 0;
const waiters = [];
const MAX_CONCURRENT = 2;
function acquire() {
  return new Promise((resolve) => {
    const run = () => {
      if (activeCaptures < MAX_CONCURRENT) { activeCaptures += 1; resolve(); }
      else waiters.push(run);
    };
    run();
  });
}
function release() {
  activeCaptures = Math.max(0, activeCaptures - 1);
  const next = waiters.shift();
  if (next) next();
}

// On-demand single-frame grab at FULL resolution from a given source URL/time —
// used by the Color stage's "Full-res check" to confirm a grade against the true
// master-resolution frame (the live preview scrubs the light 1080p proxy). Not
// concurrency-limited: it's a deliberate one-shot user action.
export function captureFrameAt({ url, streamType, atSec = 0, maxW = 1920 }) {
  return new Promise((resolve, reject) => {
    if (!url) { reject(new Error('no source url')); return; }
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    let hls = null;
    let settled = false;

    const cleanup = () => {
      try { if (hls) hls.destroy(); } catch { /* noop */ }
      try { video.removeAttribute('src'); video.load(); } catch { /* noop */ }
    };
    const fail = (e) => { if (settled) return; settled = true; cleanup(); reject(e || new Error('capture failed')); };
    const done = (val) => { if (settled) return; settled = true; cleanup(); resolve(val); };

    const waitSeek = (t) => new Promise((res) => {
      let fin = false;
      const finish = () => {
        if (fin) return;
        fin = true;
        video.removeEventListener('seeked', finish);
        requestAnimationFrame(() => requestAnimationFrame(res));
      };
      video.addEventListener('seeked', finish);
      try { video.currentTime = t; } catch { finish(); }
      setTimeout(finish, 3500);
    });

    // waitSeek's fallback timeout can fire long before a slow source (UHD HLS
    // delivery proxy, big presigned MP4) has actually buffered + decoded the
    // seeked frame. At that point metadata is in (videoWidth/Height nonzero)
    // but readyState is still HAVE_METADATA — and drawImage() of a video with
    // no decoded frame silently paints NOTHING, so the grab "succeeded" with an
    // all-black canvas. The auto-HD pull then cached + displayed it → both
    // studio panes permanently black. Wait for a real decodable frame
    // (readyState >= HAVE_CURRENT_DATA), bounded by the outer 20s timeout.
    const waitFrame = () => new Promise((res) => {
      if (video.readyState >= 2 && !video.seeking) { res(); return; }
      const evs = ['seeked', 'loadeddata', 'canplay', 'canplaythrough'];
      let iv = null;
      let hard = null;
      const finish = () => {
        evs.forEach((e) => video.removeEventListener(e, check));
        clearInterval(iv);
        clearTimeout(hard);
        res();
      };
      const check = () => { if ((video.readyState >= 2 && !video.seeking) || settled) finish(); };
      evs.forEach((e) => video.addEventListener(e, check));
      iv = setInterval(check, 250);
      hard = setTimeout(finish, 15000);
    });

    const onMeta = async () => {
      try {
        const dur = Number.isFinite(video.duration) ? video.duration : 0;
        const t = Math.min(Math.max(0, atSec), Math.max(0.05, (dur || atSec + 1) - 0.05));
        await waitSeek(t);
        await waitFrame();
        if (settled) return;
        const w = video.videoWidth;
        const h = video.videoHeight;
        // No decoded frame = nothing to draw. FAIL (caller keeps the proxy
        // frame on screen) rather than hand back a blank black canvas.
        if (!w || !h || video.readyState < 2) { fail(new Error('no frame decoded')); return; }
        const cw = Math.min(maxW, w);
        const ch = Math.max(1, Math.round((cw * h) / w));
        const cv = document.createElement('canvas');
        cv.width = cw;
        cv.height = ch;
        let rawUrl = '';
        try { cv.getContext('2d').drawImage(video, 0, 0, cw, ch); rawUrl = cv.toDataURL('image/jpeg', 0.92); } catch { rawUrl = ''; }
        done({ canvas: cv, rawUrl, w: cw, h: ch });
      } catch (e) {
        fail(e);
      }
    };

    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('error', () => fail(new Error('video load error')));

    const isHls = streamType === 'hls' || /\.m3u8(\?|$)/.test(url);
    if (isHls && !video.canPlayType('application/vnd.apple.mpegurl') && Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_e, data) => { if (data?.fatal) fail(new Error('hls fatal error')); });
    } else {
      video.src = url;
      video.load();
    }
    setTimeout(() => fail(new Error('timeout')), 20000);
  });
}

// Capture ONE representative frame from a clip's edit proxy (mid-trim by
// default), tearing the decoder down immediately so only the small canvas +
// JPEG dataURL remain. Returns { canvas, rawUrl, status }. The canvas can be fed
// to GradedCanvas to show the clip's look live; rawUrl is a cheap <img> source.
export function useClipFrame(clip, atFrac = 0.5, enabled = true) {
  const [state, setState] = useState({ canvas: null, rawUrl: '', status: 'idle' });

  const inSec = clip?.inSec || 0;
  const out = clip?.outSec && clip.outSec > 0 ? clip.outSec : clip?.sourceDurationSec || 0;
  const atSec = inSec + Math.max(0, out - inSec) * atFrac;
  const key = `${enabled ? 1 : 0}|${clip?.id}|${clip?.url || clip?.s3Key || ''}|${atSec.toFixed(2)}`;

  useEffect(() => {
    if (!enabled) { setState({ canvas: null, rawUrl: '', status: 'idle' }); return undefined; }
    let cancelled = false;
    let video = null;
    let hls = null;
    let acquired = false;

    const cleanup = () => {
      try { if (hls) hls.destroy(); } catch { /* noop */ }
      try { if (video) { video.removeAttribute('src'); video.load(); } } catch { /* noop */ }
      if (acquired) { acquired = false; release(); }
    };

    const waitSeek = (t) => new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        video.removeEventListener('seeked', finish);
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      };
      video.addEventListener('seeked', finish);
      try { video.currentTime = t; } catch { finish(); }
      setTimeout(finish, 1500);
    });

    const run = async () => {
      const url = await resolveClipUrl(clip, getAssetPlaybackUrl).catch(() => '');
      if (cancelled) return;
      if (!url) { setState({ canvas: null, rawUrl: '', status: 'error' }); return; }

      await acquire();
      if (cancelled) { release(); return; }
      acquired = true;

      video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';

      const onMeta = async () => {
        try {
          const dur = Number.isFinite(video.duration) ? video.duration : 0;
          const t = Math.min(Math.max(0, atSec), Math.max(0.05, (dur || atSec + 1) - 0.05));
          await waitSeek(t);
          if (cancelled) { cleanup(); return; }
          const w = video.videoWidth;
          const h = video.videoHeight;
          if (!w || !h) { setState({ canvas: null, rawUrl: '', status: 'error' }); cleanup(); return; }
          const cw = Math.min(320, w);
          const ch = Math.max(1, Math.round((cw * h) / w));
          const cv = document.createElement('canvas');
          cv.width = cw;
          cv.height = ch;
          let rawUrl = '';
          try {
            cv.getContext('2d').drawImage(video, 0, 0, cw, ch);
            rawUrl = cv.toDataURL('image/jpeg', 0.8);
          } catch { rawUrl = ''; }
          if (!cancelled) setState({ canvas: cv, rawUrl, status: 'ready' });
        } catch {
          if (!cancelled) setState({ canvas: null, rawUrl: '', status: 'error' });
        } finally {
          cleanup();
        }
      };

      video.addEventListener('loadedmetadata', onMeta);
      video.addEventListener('error', () => { if (!cancelled) { setState({ canvas: null, rawUrl: '', status: 'error' }); cleanup(); } });

      const isHls = clip?.streamType === 'hls' || /\.m3u8(\?|$)/.test(url);
      if (isHls && !video.canPlayType('application/vnd.apple.mpegurl') && Hls.isSupported()) {
        hls = new Hls({ enableWorker: true, lowLatencyMode: false });
        hls.loadSource(url);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_e, data) => { if (data?.fatal && !cancelled) { setState({ canvas: null, rawUrl: '', status: 'error' }); cleanup(); } });
      } else {
        video.src = url;
        video.load();
      }
    };

    setState((s) => ({ ...s, status: 'loading' }));
    run();

    return () => { cancelled = true; cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}

// Capture a single frame from ANY single-file source (used for the master MP4)
// at `atSec`, concurrency-limited. Unlike useClipFrame this targets a specific
// URL+time rather than a clip's own proxy — the master decodes/seeks reliably in
// the browser, so this is what drives live timeline-thumbnail previews/edits.
export function useFrameAt(url, atSec = 0, enabled = true) {
  const [state, setState] = useState({ rawUrl: '', status: 'idle' });
  const key = `${enabled ? 1 : 0}|${url || ''}|${(atSec || 0).toFixed(2)}`;

  useEffect(() => {
    if (!enabled || !url) { setState({ rawUrl: '', status: 'idle' }); return undefined; }
    let cancelled = false;
    let released = false;
    setState((s) => ({ ...s, status: 'loading' }));
    (async () => {
      await acquire();
      if (cancelled) { release(); released = true; return; }
      try {
        const { rawUrl } = await captureFrameAt({ url, atSec, maxW: 320 });
        if (!cancelled) setState({ rawUrl, status: 'ready' });
      } catch {
        if (!cancelled) setState({ rawUrl: '', status: 'error' });
      } finally {
        if (!released) { release(); released = true; }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}

// Capture one thumbnail per clip from the SINGLE composed master MP4 (one hidden
// decoder, sequential seeks to each clip's tile midpoint). This is the reliable
// fallback for clips whose own edit proxy can't be grabbed client-side (e.g. an
// HLS "1080p stream" with no clean single-file URL, CORS, or level issues). The
// master already has each clip's per-clip look baked in (but NOT the movie-wide
// grade), so these frames represent the clip's graded source.
// `segs` = [{ clip:{id}, tileStart, tileEnd }] in master seconds.
export function useMasterThumbs(url, segs) {
  const [frames, setFrames] = useState({}); // { [clipId]: { canvas, rawUrl } }
  const [status, setStatus] = useState('idle');

  const key = `${url}|${(segs || []).map((s) => `${s.clip.id}:${(((s.tileStart || 0) + (s.tileEnd || 0)) / 2).toFixed(2)}`).join(',')}`;

  useEffect(() => {
    if (!url || !segs || !segs.length) { setFrames({}); setStatus('idle'); return undefined; }
    let cancelled = false;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    let hls = null;

    const cleanup = () => {
      try { if (hls) hls.destroy(); } catch { /* noop */ }
      try { video.removeAttribute('src'); video.load(); } catch { /* noop */ }
    };
    const waitSeek = (t) => new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        video.removeEventListener('seeked', finish);
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      };
      video.addEventListener('seeked', finish);
      try { video.currentTime = t; } catch { finish(); }
      setTimeout(finish, 2000);
    });
    const grab = () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h) return null;
      const cw = Math.min(320, w);
      const ch = Math.max(1, Math.round((cw * h) / w));
      const cv = document.createElement('canvas');
      cv.width = cw;
      cv.height = ch;
      try {
        cv.getContext('2d').drawImage(video, 0, 0, cw, ch);
        return { canvas: cv, rawUrl: cv.toDataURL('image/jpeg', 0.8) };
      } catch {
        return null;
      }
    };

    const run = async () => {
      const dur = Number.isFinite(video.duration) ? video.duration : 0;
      const out = {};
      for (const s of segs) {
        if (cancelled) return;
        const mid = ((s.tileStart || 0) + (s.tileEnd || 0)) / 2;
        const t = Math.min(Math.max(0, mid), Math.max(0.05, (dur || mid + 1) - 0.05));
        // eslint-disable-next-line no-await-in-loop
        await waitSeek(t);
        if (cancelled) return;
        const f = grab();
        if (f) { out[s.clip.id] = f; setFrames({ ...out }); }
      }
      if (!cancelled) { setStatus('ready'); cleanup(); }
    };

    const onMeta = () => { run().catch(() => { if (!cancelled) setStatus('error'); }); };
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('error', () => { if (!cancelled) setStatus('error'); });

    setStatus('loading');
    setFrames({});
    const isHls = /\.m3u8(\?|$)/.test(url);
    if (isHls && !video.canPlayType('application/vnd.apple.mpegurl') && Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_e, data) => { if (data?.fatal && !cancelled) setStatus('error'); });
    } else {
      video.src = url;
      video.load();
    }

    return () => { cancelled = true; cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { frames, status };
}
